<template>
  <div v-if="canUseNativeShare" class="native-share">
    <button class="btn btn--big native-share-btn" @click="showNativeShare">
      <ShareIcon class="share-icon" />{{ nativeShareTitle }}
    </button>
  </div>
  <div class="social-share" v-else>
    <div class="social-share__links" :class="{ mini: inline, inline: inline }">
      <a
        class="social-share__link social-share__link--whatsapp social-share__link--mobile"
        :href="whatsAppShareLink"
        @click="whatsAppShareHandler"
        target="_blank"
        ><span class="social-share__link-icon"
          ><WhatsAppIcon width="27px" height="27px" aria-role="presentation" />
        </span>
        <span class="social-share__link-text" v-if="!inline">WhatsApp</span>
      </a>
      <a
        class="social-share__link social-share__link--whatsapp social-share__link--desktop"
        :href="whatsAppShareLinkDesktop"
        @click="whatsAppShareHandler"
        target="_blank"
        ><span class="social-share__link-icon"
          ><WhatsAppIcon width="27px" height="27px" aria-role="presentation"
        /></span>
        <span class="social-share__link-text" v-if="!inline">WhatsApp</span>
      </a>
      <a
        class="social-share__link social-share__link--messenger"
        :href="facebookMessengerShareLink"
        @click.prevent="facebookShareHandler"
        ><span class="social-share__link-icon"
          ><FacebookIcon width="14px" height="26px" aria-role="presentation" />
        </span>
        <span class="social-share__link-text" v-if="!inline"
          >Facebook Messenger</span
        >
      </a>
      <a
        class="social-share__link social-share__link--email"
        :href="emailShareLink"
        @click="emailShareHandler"
        target="_blank"
        ><span class="social-share__link-icon">
          <EnvelopeIcon width="25px" height="19px" aria-role="presentation" />
        </span>
        <span class="social-share__link-text" v-if="!inline">Email</span>
      </a>
    </div>

    <form class="form social-share__form" :class="{ inline: inline }">
      <fieldset class="form__fieldset">
        <div class="form__inline">
          <input
            type="url"
            class="form__input"
            id="share-link"
            data-testid="share-link"
            name="share-link"
            v-model="shareLink"
            readonly
          />
          <button
            type="button"
            class="btn share-link__copy"
            data-clipboard-target="#share-link"
            ref="copyBtn"
            @click="copyShareLinkHandler"
          >
            Copy Link
          </button>
        </div>
      </fieldset>
    </form>
  </div>
</template>

<script>
import ClipboardJS from "clipboard";
import { isNativeShareSupported, nativeShare } from "@/utility/nativeShare";
import FacebookIcon from "@/assets/svg/logo-facebook.svg";
import WhatsAppIcon from "@/assets/svg/logo-whatsapp.svg";
import EnvelopeIcon from "@/assets/svg/icon-envelope.svg";
import ShareIcon from "@/assets/svg/icon-share.svg";

export default {
  name: "SocialShare",
  props: {
    shareLink: {
      type: String,
      required: true,
    },
    nativeShareTitle: {
      type: String,
      required: true,
    },
    shareSubject: {
      type: String,
      required: true,
    },
    shareBody: {
      type: String,
      required: true,
    },
    emailViaModal: {
      type: Boolean,
      default: false,
    },
    whatsAppShareBody: {
      type: String,
      required: true,
    },
    facebookTitle: {
      type: String,
      required: true,
    },
    facebookDescription: {
      type: String,
      required: true,
    },
    facebookImageUrl: {
      type: String,
      required: true,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      facebookAppId: 270079770569051,
      isShareInProgress: false,
    };
  },
  computed: {
    whatsAppShareLink() {
      return `https://api.whatsapp.com/send?text=${encodeURIComponent(
        this.whatsAppShareBody,
      )}${encodeURIComponent(this.shareLink)}`;
    },
    whatsAppShareLinkDesktop() {
      return `https://web.whatsapp.com/send?text=${encodeURIComponent(
        this.whatsAppShareBody,
      )}${encodeURIComponent(this.shareLink)}`;
    },
    facebookMessengerShareLink() {
      return `fb-messenger://share/?link=${encodeURIComponent(
        this.shareLink,
      )}&app_id=${this.facebookAppId}`;
    },
    emailShareLink() {
      return `mailto:?subject=${this.shareSubject}&body=${encodeURIComponent(
        this.shareBody,
      )}${encodeURIComponent(this.shareLink)}`;
    },
    canUseNativeShare() {
      return isNativeShareSupported();
    },
  },
  methods: {
    whatsAppShareHandler() {
      this.$emit("whatsAppShare");
    },
    facebookShareHandler() {
      this.$emit("facebookShare");

      window.FB.ui(
        {
          method: "share",
          display: "popup",
          href: this.shareLink,
        },
        function(response) {
          if (response) {
            console.log(response);  // eslint-disable-line
          }
        },
      );
    },
    emailShareHandler(event) {
      this.$emit("emailShare");

      if (this.emailViaModal) {
        event.preventDefault();

        this.$emit("show-modal");

        return false;
      } else {
        return true;
      }
    },
    copyShareLinkHandler() {
      this.$emit("copyShareLink");
    },
    showNativeShare() {
      if (!this.isShareInProgress) {
        this.isShareInProgress = true;

        this.$emit("nativeShare");

        nativeShare({
          title: this.shareSubject,
          text: this.shareBody + this.shareLink,
          //url: this.shareLink
        }).finally(() => {
          this.isShareInProgress = false;
        });
      }
    },
  },
  mounted() {
    if (!this.canUseNativeShare) {
      this.clipboard = new ClipboardJS(this.$refs.copyBtn);
    }
  },
  beforeDestroy() {
    if (this.clipboard) {
      this.clipboard.destroy();
    }
  },
  components: {
    WhatsAppIcon,
    FacebookIcon,
    EnvelopeIcon,
    ShareIcon,
  },
};
</script>

<style lang="scss" scoped>
.native-share {
  margin-top: $spacing-small;

  .native-share-btn {
    display: flex;
    justify-content: center;
    align-items: baseline;

    background-color: $brand-orange;
    border-radius: $border-radius-small;

    &:hover {
      background-color: darken($brand-orange, 5%);
    }
  }

  .share-icon {
    margin-right: 1rem;
  }
}

.social-share {
  display: flex;
  flex-direction: column;
  margin-bottom: $spacing-medium;
}

.social-share__link {
  display: flex;
  min-width: 230px;
  text-decoration: none;
  text-align: left;
  font-size: 1.6rem;
  font-weight: 100;
  margin-bottom: $spacing-medium;

  &:last-child {
    margin-bottom: 0;
  }

  &.social-share__link--whatsapp {
    .social-share__link-icon {
      color: $white;
      background-color: #25d366;
    }

    .social-share__link-text {
      color: $white;
      background-color: #25d366;
    }
  }

  &.social-share__link--messenger {
    .social-share__link-icon {
      color: $white;
      background-color: #4267b2;
    }

    .social-share__link-text {
      color: $white;
      background-color: #4267b2;
    }
  }

  &.social-share__link--email {
    .social-share__link-icon {
      background: $grey-faint;

      &.inline {
        background: white;
      }

      color: $black;
    }
  }

  &.social-share__link--mobile {
    @include media(">=medium") {
      display: none;
    }
  }

  &.social-share__link--desktop {
    @include media("<medium") {
      display: none;
    }
  }
}

.social-share__links {
  &.inline {
    order: 2;
    max-width: 30rem;
    margin: 0 auto;

    @include media("<medium") {
      max-width: 20rem;
    }

    .social-share__link-icon {
      box-shadow: $brand-box-shadow;
    }
  }

  &.mini {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;

    .social-share__link {
      min-width: auto;
      margin-bottom: 0;
    }

    .social-share__link-icon {
      margin-right: 0;
    }
  }
}

.social-share__link-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 42px;
  height: 42px;
  border-radius: $border-radius-small;
  padding: 5px;
  margin-right: $spacing-small;

  flex-shrink: 0;
}

.social-share__link-text {
  flex-basis: 100%;

  background-color: $grey-faint;
  border-radius: $border-radius-small;
  padding: 9px $spacing-medium;

  font-weight: $font-regular;
}

.social-share__form {
  margin-top: $spacing-medium;

  .form__fieldset {
    border: 2px solid $grey-mid;
    border-radius: $border-radius-small;
  }

  &.inline {
    order: 1;
  }

  input {
    padding: 0;
    border: none;
    background: none;
    flex-shrink: 1;
    margin: 0 $spacing-small;

    &:focus {
      outline: none;
    }
  }

  button {
    font-size: 18px;
    flex-shrink: 0;
    background: $brand-green;
    color: $white;
    padding: $spacing-tiny $spacing-xs;
    margin: $spacing-tiny $spacing-tiny $spacing-tiny 0;
    border: none;
    border-radius: $border-radius-small;

    &:focus {
      outline: none;
    }
  }
}
</style>
